import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import type { ClusterDetailResponseOutputPhotosItem } from '@/api/generated';
import {
  type ClusterMark,
  MARK_STYLES,
  MarkIcon,
  PhotoFrame,
} from '@/components/blocks/clusters/cluster-photo';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface ClusterCompareDialogProps {
  original: ClusterDetailResponseOutputPhotosItem;
  selected: ClusterDetailResponseOutputPhotosItem | null;
  mark: ClusterMark | undefined;
  onClose: () => void;
  onMark: (mark: ClusterMark) => void;
  // Clear a Junk / Wrong SKU mark, back to approved.
  onApprove: () => void;
  // Someone else has the cluster open: only looking, no marking.
  readOnly?: boolean;
}

const Kbd: FC<{ children: string }> = ({ children }) => (
  <kbd className='rounded border border-current px-1 font-mono text-[10px] opacity-60'>
    {children}
  </kbd>
);

/** Original vs selected, front next to front and back next to back. */
export const ClusterCompareDialog: FC<ClusterCompareDialogProps> = ({
  original,
  selected,
  mark,
  onClose,
  onMark,
  onApprove,
  readOnly = false,
}: ClusterCompareDialogProps) => {
  const { t } = useTranslation();
  // The photo that opened the dialog, to hand focus back to on close.
  const opener = useRef<HTMLElement | null>(null);
  const scrollY = useRef(0);

  const pair = (side: 'front' | 'back') => (
    <section className='flex flex-col gap-2'>
      <h3 className='text-lg font-semibold'>
        {side === 'front'
          ? t('clusters.review.front')
          : t('clusters.review.back')}
      </h3>
      <div className='grid grid-cols-2 gap-2'>
        {[original, selected].map((photo, index) => (
          <figure
            key={index}
            className='relative m-0'>
            <span
              className={cn(
                'absolute top-2 left-2 z-10 rounded px-2 py-0.5 text-xs font-bold',
                index === 0
                  ? 'bg-brand text-neutral-950'
                  : 'bg-foreground text-background'
              )}>
              {index === 0
                ? t('clusters.review.original')
                : t('clusters.review.selected')}
            </span>
            <PhotoFrame
              className={cn(
                'aspect-auto h-[min(55vh,520px)] border-4 border-transparent',
                index === 0 && 'border-brand'
              )}
              src={photo?.[side] ?? null}
              alt={t('clusters.review.noBack')}
            />
          </figure>
        ))}
      </div>
    </section>
  );

  return (
    <Dialog
      open={!!selected}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}>
      <DialogContent
        data-testid='cluster-compare-dialog'
        className='max-w-6xl'
        onOpenAutoFocus={() => {
          opener.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
          scrollY.current = window.scrollY;
        }}
        // The scroll lock behind the dialog can leave the page somewhere
        // else once it is released; put the reviewer back where they were,
        // and hand focus back to the photo without scrolling to it.
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          opener.current?.focus({ preventScroll: true });
          const top = scrollY.current;
          requestAnimationFrame(() => {
            window.scrollTo({ top });
          });
        }}
        onKeyDown={(event) => {
          const key = event.key.toLowerCase();
          if (readOnly || event.metaKey || event.ctrlKey || event.altKey)
            return;
          if (key === 'j') onMark('junk');
          else if (key === 'w') onMark('wrongSku');
          else if (key === 'a' && mark) onApprove();
        }}>
        <DialogTitle>{t('clusters.review.compareTitle')}</DialogTitle>
        <DialogDescription className='sr-only'>
          {t('clusters.review.hint')}
        </DialogDescription>
        <div className='grid gap-8 lg:grid-cols-2'>
          {pair('front')}
          {pair('back')}
        </div>
        <div className='flex flex-wrap items-center gap-2 border-t pt-4'>
          <Button
            data-testid='cluster-compare-close'
            variant='outline'
            size='lg'
            onClick={onClose}>
            {t('clusters.review.close')} <Kbd>Esc</Kbd>
          </Button>
          <span className='flex-1' />
          {mark && !readOnly && (
            <Button
              data-testid='cluster-compare-approve'
              variant='outline'
              size='lg'
              onClick={onApprove}>
              <Check />
              {t('clusters.review.approve')}
              <Kbd>A</Kbd>
            </Button>
          )}
          {(['junk', 'wrongSku'] as const).map((kind) => (
            <Button
              data-testid={`cluster-compare-${kind === 'junk' ? 'junk' : 'wrong-sku'}`}
              key={kind}
              variant='outline'
              size='lg'
              disabled={readOnly}
              aria-pressed={mark === kind}
              className={cn(mark === kind && MARK_STYLES[kind])}
              onClick={() => {
                onMark(kind);
              }}>
              <MarkIcon mark={kind} />
              {kind === 'junk'
                ? t('clusters.review.junk')
                : t('clusters.review.wrongSku')}
              <Kbd>{kind === 'junk' ? 'J' : 'W'}</Kbd>
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
